/**
 * ESocialEventsTable Component
 * Table displaying eSocial events with actions
 */

import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Typography,
  Tooltip,
  TablePagination,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SubmitIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import {
  ESocialEvent,
  ESocialEventStatus,
  ESOCIAL_EVENT_TYPE_SHORT_LABELS,
  canEventBeEdited,
  canEventBeSubmitted,
  canEventBeCancelled,
  canEventBeDeleted
} from '../../../../types/esocial';
import ESocialEventStatusChip from './ESocialEventStatusChip';

// Status em que o evento ainda vai ser enviado — só neles a ficha editada à mão pesa
const STATUSES_TO_SEND: ESocialEventStatus[] = [
  ESocialEventStatus.DRAFT,
  ESocialEventStatus.PENDING,
  ESocialEventStatus.ERROR,
  ESocialEventStatus.REJECTED
];

const isManuallyEditedToSend = (event: ESocialEvent) =>
  !!event.metadataEditedAt && STATUSES_TO_SEND.includes(event.eventStatus);

interface ESocialEventsTableProps {
  events: ESocialEvent[];
  loading?: boolean;
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onView: (event: ESocialEvent) => void;
  onEdit: (event: ESocialEvent) => void;
  onDelete: (event: ESocialEvent) => void;
  onSubmit: (event: ESocialEvent) => void;
  onRetry?: (event: ESocialEvent) => void;
  onCancel: (event: ESocialEvent) => void;
}

const ESocialEventsTable: React.FC<ESocialEventsTableProps> = ({
  events,
  loading = false,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  onView,
  onEdit,
  onDelete,
  onSubmit,
  onRetry,
  onCancel
}) => {
  const isRetryable = (status: string) => status === 'ERROR' || status === 'REJECTED';

  const handleSubmitOrRetry = (event: ESocialEvent) => {
    if (onRetry && isRetryable(event.eventStatus)) {
      onRetry(event);
    } else {
      onSubmit(event);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onLimitChange(parseInt(event.target.value, 10));
    onPageChange(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Funcionário</TableCell>
              <TableCell>Data do Evento</TableCell>
              <TableCell>Data Limite</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Recibo</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    Nenhum evento encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Tooltip title={ESOCIAL_EVENT_TYPE_SHORT_LABELS[event.eventType]}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ESOCIAL_EVENT_TYPE_SHORT_LABELS[event.eventType]}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {event.employee?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {formatDate(event.eventDate)}
                  </TableCell>
                  <TableCell>
                    {event.dueDate ? formatDate(event.dueDate) : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <ESocialEventStatusChip status={event.eventStatus} />
                      {isManuallyEditedToSend(event) && (
                        <Tooltip title="A ficha deste evento foi alterada à mão e será enviada como foi editada, sem considerar mudanças posteriores no cadastro.">
                          <Chip label="Editado à mão" size="small" variant="outlined" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {event.receiptNumber ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Visualizar">
                        <IconButton
                          size="small"
                          onClick={() => onView(event)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {canEventBeEdited(event.eventStatus) && (
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(event)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canEventBeSubmitted(event.eventStatus) && (
                        <Tooltip title={isRetryable(event.eventStatus) ? 'Reenviar' : 'Transmitir'}>
                          <IconButton
                            size="small"
                            color={isRetryable(event.eventStatus) ? 'warning' : 'primary'}
                            onClick={() => handleSubmitOrRetry(event)}
                          >
                            <SubmitIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canEventBeCancelled(event.eventStatus) && (
                        <Tooltip title="Cancelar">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => onCancel(event)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canEventBeDeleted(event.eventStatus) && (
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(event)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Itens por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Paper>
  );
};

export default ESocialEventsTable;
